import { useEffect, useLayoutEffect } from 'react'
import { arrowBlockSelection, decoratorLineMove, escapeSelection, exitNodeSelection, extendBlockSelection, isSelectableBlock, nodeSelection,
  ownBlock, toggleBlockSelection } from '../../core/block-selection'
import { blockGap, findBlock, gapPoint, isRow, locate, plain, pointAt, rows } from '../../core/tree-read'
import type { Block } from '@w/content-blocks'
import type { Overlay } from '../view-api'
import { blockElement, focusedBlock, select, topBlock, useLiveApi, type OverlayApi } from './shared'
import { EDITABLE } from '../dom-selection'

/** An atom's own controls and fields handle their clicks; only a click on the atom's surface selects it. */
const ATOM_CONTROLS = ['input', 'textarea', 'select', 'button', 'a', 'label', '[role="button"]', '[role="checkbox"]', '[role="radio"]',
  '[role="switch"]', '[role="menuitem"]', '[data-core-field]',
  `${EDITABLE}:not([data-testid="content-blocks-editor-root"])`].join(', ')

const selectedClasses = ['rounded-sm', 'max-[1025px]:touch-none', 'bg-primary/8', 'outline-2', 'outline-primary/60', 'outline-offset-2']

/** The block a pointer targets, as Notion counts blocks (core `ownBlock`, the block Esc selects). */
function pointerBlock(api: OverlayApi, target: EventTarget | null): Block | undefined {
  const id = target instanceof Element ? target.closest<HTMLElement>('[data-block-id]')?.dataset.blockId : undefined
  return (id === undefined ? undefined : ownBlock(api.state.blocks, id)) ?? topBlock(api, target)
}

export const BlockSelection: Overlay = ({ api }: { api: OverlayApi }) => {
  const live = useLiveApi(api)
  useLayoutEffect(() => {
    const projected: HTMLElement[] = []
    for (const id of api.state.selection?.nodes ?? []) {
      const element = blockElement(api, id)
      if (!element) continue
      element.dataset.blockSelected = 'true'
      element.dataset.contentBlockSelected = 'true'
      element.classList.add(...selectedClasses)
      projected.push(element)
    }
    return () => {
      for (const element of projected) {
        delete element.dataset.blockSelected
        delete element.dataset.contentBlockSelected
        element.classList.remove(...selectedClasses)
      }
    }
  }, [api])
  useEffect(() => {
    const root = api.root
    if (!root || api.mode !== 'edit') return () => {}
    function collapse(end: boolean) {
      const api = live.current
      const selection = api.state.selection
      const id = end ? selection?.nodes?.at(-1) : selection?.nodes?.[0]
      const gap = id === undefined ? undefined : blockGap(api.state.blocks, id)
      const block = gap?.siblings[gap.index]
      if (!gap || !block) return
      const entries = rows([block])
      const row = end ? entries.at(-1) : entries[0]
      const point = row ? pointAt(row, end ? plain(row.block).length : 0)
        : gapPoint({ ...gap, index: gap.index + (end ? 1 : 0) })
      select(api, { anchor: point, focus: point })
    }
    const keydown = (event: KeyboardEvent) => {
      const api = live.current
      const selection = api.state.selection
      if (!selection || event.defaultPrevented || event.isComposing) return
      // A focused control or field inside an atom owns its keys, as it owns its clicks.
      if (event.target instanceof Element && event.target.closest(ATOM_CONTROLS)) return
      const nodes = selection.nodes
      const block = focusedBlock(api)
      let handled = false
      if (event.key === 'Escape' && block) {
        // Notion Esc selects the block the caret is in; Esc again clears the selected blocks (core `escapeSelection`).
        select(api, escapeSelection(api.state))
        handled = true
      } else if (nodes?.length) {
        if (event.key === 'Enter') { collapse(true); handled = true }
        else if (event.key.startsWith('Arrow') && !event.metaKey && !event.ctrlKey && !event.altKey) {
          const vertical = ['ArrowUp', 'ArrowDown'].includes(event.key)
          const selectedBlock = findBlock(api.state.blocks, nodes[0])
          // Notion arrow keys move or extend the selected blocks (core `arrowBlockSelection`); Shift+Left/Right extends
          // as Shift+Up/Down does, so the selection never drops (rule nux/selection/shift-arrow-from-selected-atom).
          if (vertical || event.shiftKey) {
            const next = arrowBlockSelection(api.state, ['ArrowUp', 'ArrowLeft'].includes(event.key) ? 'up' : 'down', event.shiftKey)
            if (next) select(api, next)
          } else if (nodes.length === 1 && selectedBlock && isSelectableBlock(selectedBlock)) {
            select(api, exitNodeSelection(api.state, event.key === 'ArrowRight' ? 'down' : 'up'))
          } else collapse(event.key === 'ArrowRight')
          handled = true
        } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'a') {
          // The Mod+A key on selected blocks (core `selectAll` stepwise): every block selected stays; otherwise the body
          // range, as from a caret: replacing it replaces everything it covers, a lone block too.
          api.dispatch({ type: 'selectAll', stepwise: true })
          handled = true
        } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'd') {
          api.dispatch({ type: 'duplicateBlock' }); handled = true
        } else if (event.key === 'Backspace' || event.key === 'Delete') {
          api.dispatch({ type: event.key === 'Backspace' ? 'deleteContentBackward' : 'deleteContentForward' }); handled = true
        }
      } else if (['ArrowUp', 'ArrowDown'].includes(event.key) && !event.shiftKey && !event.metaKey && !event.ctrlKey) {
        const element = block && blockElement(api, block.id)
        const dom = root.ownerDocument.getSelection()
        const rect = dom?.rangeCount ? dom.getRangeAt(0).getBoundingClientRect() : null
        const bounds = element?.getBoundingClientRect()
        const entries = rows(api.state.blocks)
        const at = locate(entries, selection.focus)
        const text = entries[at.row] ? plain(entries[at.row].block) : ''
        const onEdgeLine = !(event.key === 'ArrowUp' ? text.slice(0, at.offset) : text.slice(at.offset)).includes('\n')
        const leaves = onEdgeLine && (!rect?.height || !bounds?.height || (event.key === 'ArrowUp' ? rect.top <= bounds.top + 1 : rect.bottom >= bounds.bottom - 1))
        const next = decoratorLineMove(api.state, event.key === 'ArrowUp' ? 'up' : 'down', leaves)
        if (next) { select(api, next); handled = true }
      }
      if (handled) { event.preventDefault(); event.stopImmediatePropagation() }
    }
    const beforeInput = (event: InputEvent) => {
      if (!live.current.state.selection?.nodes?.length) return
      if (event.inputType === 'duplicateBlock') {
        event.preventDefault(); event.stopImmediatePropagation()
        live.current.dispatch({ type: 'duplicateBlock' })
      }
    }
    const shift = (event: MouseEvent) => {
      const api = live.current
      const selection = api.state.selection
      const block = pointerBlock(api, event.target)
      if (event.shiftKey && selection && block && (event.metaKey || event.ctrlKey || event.altKey)) {
        // Notion keyboard shortcuts: Cmd+Shift+click (Alt+Shift+click on Windows/Linux) selects or deselects one block.
        event.preventDefault()
        event.stopImmediatePropagation()
        // The press toggles; its click only stays out of the text selection.
        if (event.type !== 'mousedown') return
        select(api, toggleBlockSelection(api.state, block.id))
      } else if (event.shiftKey && selection?.nodes?.length && block) {
        event.preventDefault()
        event.stopImmediatePropagation()
        select(api, extendBlockSelection(api.state, block.id))
      } else if (event.type === 'click' && event.target instanceof Element &&
        !event.target.closest(ATOM_CONTROLS)) {
        // A click on an atom's own surface selects that atom, wherever it sits (Lexical decorators' click selection);
        // a click inside a row is a text click, even on an inline leaf or an inline atom.
        const owners: Block[] = []
        for (let element = event.target.closest<HTMLElement>('[data-block-id]'); element; element = element.parentElement?.closest<HTMLElement>('[data-block-id]') ?? null) {
          const owner = element.dataset.blockId === undefined ? undefined : findBlock(api.state.blocks, element.dataset.blockId)
          if (owner) owners.push(owner)
        }
        const atom = owners[0]
        if (!atom || !isSelectableBlock(atom) || owners.some(owner => isRow(owner))) return
        event.preventDefault()
        event.stopImmediatePropagation()
        select(api, nodeSelection(atom.id))
      }
    }
    const outside = (event: PointerEvent) => {
      if (!(event.target instanceof Element) || root.contains(event.target) || event.target.closest('[data-core-overlay]')) return
      if (live.current.state.selection?.nodes?.length) select(live.current, null)
    }
    root.addEventListener('keydown', keydown, true)
    root.addEventListener('beforeinput', beforeInput, true)
    root.addEventListener('mousedown', shift, true)
    root.addEventListener('click', shift, true)
    document.addEventListener('pointerdown', outside)
    return () => {
      root.removeEventListener('keydown', keydown, true)
      root.removeEventListener('beforeinput', beforeInput, true)
      root.removeEventListener('mousedown', shift, true)
      root.removeEventListener('click', shift, true)
      document.removeEventListener('pointerdown', outside)
    }
  }, [api.root, api.mode, live])
  return null
}
